import { Link } from "react-router-dom";
import { Instagram, Mail, MessageCircle, MapPin } from "lucide-react";
import { BRAND, openWhatsapp } from "@/lib/contact";

export default function Footer() {
    const year = new Date().getFullYear();

    const goTo = (id) => (e) => {
        e.preventDefault();
        const el = document.querySelector(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <footer
            data-testid="site-footer"
            className="relative bg-[var(--uma-cream)] border-t border-[var(--uma-forest)]/10"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Brand */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3">
                            <img
                                src="/logo-altez.png"
                                alt="Altez Palarin Estudio Contable"
                                className="h-16 w-auto object-contain"
                            />
                            <div className="leading-tight">
                                <div className="font-serif-display text-[28px] text-[var(--uma-forest)]">
                                    Altez Palarin
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--uma-text-soft)] mt-1">
                                    Estudio Contable
                                </div>
                            </div>
                        </div>

                        <p className="mt-6 max-w-md text-[14px] leading-relaxed text-[var(--uma-text-soft)] font-light">
                            Contabilidad preventiva, asesoramiento fiscal y
                            acompañamiento integral para empresas y
                            emprendedores. Sedes en Rocha y Castillos —
                            atendemos a todo el Uruguay.
                        </p>

                        <div className="mt-7 flex items-center gap-3">
                            <button
                                data-testid="footer-whatsapp"
                                onClick={() => openWhatsapp()}
                                aria-label="WhatsApp"
                                className="w-10 h-10 rounded-full border border-[var(--uma-forest)]/15 text-[var(--uma-forest)] hover:bg-[var(--uma-forest)] hover:text-[var(--uma-cream)] flex items-center justify-center transition-all"
                            >
                                <MessageCircle className="h-4 w-4" />
                            </button>
                            <a
                                href={BRAND.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-instagram"
                                aria-label="Instagram"
                                className="w-10 h-10 rounded-full border border-[var(--uma-forest)]/15 text-[var(--uma-forest)] hover:bg-[var(--uma-forest)] hover:text-[var(--uma-cream)] flex items-center justify-center transition-all"
                            >
                                <Instagram className="h-4 w-4" />
                            </a>
                            <a
                                href={`mailto:${BRAND.email}`}
                                data-testid="footer-email"
                                aria-label="Email"
                                className="w-10 h-10 rounded-full border border-[var(--uma-forest)]/15 text-[var(--uma-forest)] hover:bg-[var(--uma-forest)] hover:text-[var(--uma-cream)] flex items-center justify-center transition-all"
                            >
                                <Mail className="h-4 w-4" />
                            </a>
                        </div>
                    </div>

                    {/* Links */}
                    <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--uma-text-soft)] mb-5">
                                Navegación
                            </div>
                            <ul className="space-y-3">
                                {[
                                    ["Servicios", "#servicios"],
                                    ["Nosotros", "#nosotros"],
                                    ["Por qué elegirnos", "#diferenciales"],
                                    ["Para quién trabajamos", "#para-quien"],
                                    ["Contacto", "#contacto"],
                                ].map(([label, href]) => (
                                    <li key={href}>
                                        <a
                                            href={href}
                                            onClick={goTo(href)}
                                            data-testid={`footer-nav-${href.slice(1)}`}
                                            className="text-[14px] text-[var(--uma-forest)] hover:text-[var(--uma-gold)] transition-colors font-medium"
                                        >
                                            {label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--uma-text-soft)] mb-5">
                                Sedes
                            </div>
                            <ul className="space-y-4 text-[13px]">
                                {BRAND.sedes.map((s, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-2"
                                    >
                                        <MapPin className="h-3.5 w-3.5 text-[var(--uma-gold)] flex-shrink-0 mt-0.5" />
                                        <div>
                                            <div className="font-medium text-[var(--uma-forest)]">
                                                {s.city}
                                            </div>
                                            <div className="text-[var(--uma-text-soft)] font-light">
                                                {s.address}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                                <li className="pt-2">
                                    <Link
                                        to="/agro"
                                        data-testid="footer-vert-agro"
                                        className="text-[13px] text-[var(--uma-gold)] hover:text-[var(--uma-forest)] font-medium transition-colors inline-flex items-center gap-1.5"
                                    >
                                        Servicios Agro →
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--uma-text-soft)] mb-5">
                                Contacto
                            </div>
                            <ul className="space-y-3">
                                <li className="text-[var(--uma-forest)] font-medium text-[14px]">
                                    {BRAND.whatsappDisplay}
                                </li>
                                <li className="text-[var(--uma-text-soft)] font-light text-[12px] break-all leading-relaxed">
                                    <a
                                        href={`mailto:${BRAND.email}`}
                                        className="hover:text-[var(--uma-gold)] transition-colors"
                                    >
                                        {BRAND.email}
                                    </a>
                                </li>
                                <li className="text-[var(--uma-text-soft)] font-light text-[13px]">
                                    Lun a Vie · 9 a 18 hs
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-[var(--uma-forest)]/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="text-[12px] text-[var(--uma-text-soft)] font-light">
                        © {year} Estudio Altez Palarin. Todos los derechos reservados.
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-[var(--uma-text-soft)]/70">
                        <span className="font-light">Sitio desarrollado por</span>
                        <a
                            href={BRAND.developerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-uma-credit"
                            className="font-medium text-[var(--uma-text-soft)] hover:text-[var(--uma-gold)] transition-colors"
                        >
                            UMA Agency
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
